import { useCurrentUser } from '../../auth/useCurrentUser'

/**
 * Vekil modu durumu (089).
 *
 * `targetDisplayName` **adına çalışılan** kişinin adıdır (uyarı şeridinde gösterilir);
 * `actingDisplayName` işlemi fiilen yapan gerçek kullanıcıdır. Sunucu yanıtı gelmeden `active`
 * `false` döner — belirsizlikte "vekil modunda değiliz" varsayılır ki şerit yanlış yere basılmasın.
 */
export function useProxyMode() {
  const { data, isLoading } = useCurrentUser()
  return {
    active: data?.proxy?.active ?? false,
    targetDisplayName: data?.displayName,
    actingUserId: data?.proxy?.actingUserId ?? null,
    actingDisplayName: data?.proxy?.actingDisplayName ?? null,
    kind: data?.proxy?.kind ?? null,
    /**
     * **Kısıtlı Yetkili asistan kipi** — arayüz hiçbir yazma eylemi SUNMAMALIDIR (126 FR-019:
     * *"sunulup hata verilmemelidir"*). Tek istisna Ajanda katılım yanıtıdır (26.10.2026); o panel
     * bu bayrağa bilinçle bağlı değildir.
     *
     * <p><b>Neden yetki kümesi yetmiyor:</b> yetki daraltması yalnız yazmayı AYRI bir kodla koruyan
     * yüzeyleri kapatır. Okuma ile yazmayı tek kodda birleştiren modüllerde (Ajanda → `AGENDA_VIEW`)
     * kod listede kalmak ZORUNDA — asistanın daveti görmesi isteğin kendi tanımında var (FR-020) —
     * ama aynı kod "yeni ajanda" düğmesini de açıyor. Sunucu tarafında bunun karşılığı
     * `ProxyValidationFilter`'ın HTTP metodu yasağıdır (research D10); bu bayrak onun arayüz
     * karşılığıdır.
     *
     * <p><b>Yükleme sırasında FAIL-CLOSED</b> ({@code isLoading} iken {@code true}). Gerekçe ölçüldü
     * (push kapısı review'ü): Ajanda'nın iki rotası bilinçle {@code RequireGrant}'siz, yani {@code /me}
     * gelmeden mount oluyorlar. Fail-open olsaydı Kısıtlı asistan doğrudan adrese girdiğinde ya da
     * sayfayı yenilediğinde yazma düğmeleri bir an çizilip sonra kaybolurdu. Bedeli, yetkisi olan
     * kullanıcıda düğmenin bir an GEÇ gelmesidir — yanlış bir eylem sunmaktansa bu yeğdir.
     *
     * <p>Yine de <b>güvenlik sınırı değildir</b>: yazmayı sunucu reddediyor, bu bayrak yalnız
     * kullanıcıya çalışmayan düğme göstermemek için.
     */
    readOnly: isLoading || (data?.proxy?.kind ?? null) === 'ASSISTANT_LIMITED',
    /**
     * **Giriş yapan kişinin** kendi profili var mı — kipten BAĞIMSIZ doğru cevap.
     *
     * <p>Persona kipinde üst düzey `roleIds` **hedefin**dir; doğrudan okunursa rolsüz bir asistan,
     * yöneticinin kipindeyken yöneticinin rollerini görür ve "kendi profilim var" sanılır
     * (kullanıcı bildirimi 21.10.2026: kipteyken "Kendim" satırı hâlâ çıkıyordu). Kipteyken
     * sunucunun verdiği `actingHasOwnProfile` okunur, kendi personasında `roleIds`.
     *
     * <p><b>Alan eksik gelirse FAIL-OPEN</b> — `readOnly`'nin tersi, ve bu bilinçlidir. Ölçüt
     * "profili olduğunu biliyoruz" değil, **"profilinin OLMADIĞINI biliyoruz"**: iki alan da bu
     * commit'le doğdu, yani frontend imajı backend'den önce yayına girerse (THY'de `frontend/` ve
     * `backend/` ayrı depo + ayrı pipeline) `/me` onları hiç göndermez. `=== true` yazılsaydı o
     * pencerede **rolü olan** bir vekil de "profili yok" sayılır, "Kendim" satırı çizilmez ve
     * kullanıcı kendi kimliğine **dönemezdi** — FR-017'nin "tek adımda çıkış" şartı düşerdi. Oysa
     * spec o kilitlenmeyi yalnız rolsüz asistan için kabul ediyor. Aynı fail-open sözleşmesi
     * `useCurrentUser`'da (*"okuyanlar korunur → hiçbir şey gizlenmez"*) ve
     * `useAssistantAutoPersona`'nın `Array.isArray` ölçütünde yazılı; burada da aynısı uygulanır.
     */
    hasOwnProfile: data?.proxy?.active
      ? data.proxy.actingHasOwnProfile !== false
      : !(Array.isArray(data?.roleIds) && data.roleIds.length === 0),
  }
}
